import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiSave, FiRotateCcw, FiSearch, FiGlobe, FiPhone, FiShare2, FiTrendingUp,
  FiAlignLeft, FiCode, FiSettings, FiAlertTriangle, FiInbox, FiBarChart2,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Spinner, Badge } from '../../components/common';
import CmsField from './components/CmsField';
import { settingsService, getApiError } from '../../services/cmsService';
import './SiteSettingsPage.css';

const GROUP_META = {
  general: { label: 'General', icon: FiGlobe, description: 'Site name, logo and the basics shown across every page.' },
  stats: { label: 'Stats', icon: FiBarChart2, description: 'The counter figures shown on Home, About, Clients and Gallery. Each number is written once here and reused everywhere it appears.' },
  contact: { label: 'Contact', icon: FiPhone, description: 'Phone, email, address and map — used in the header, footer and contact page.' },
  social: { label: 'Social', icon: FiShare2, description: 'Profile links shown in the footer and share widgets.' },
  seo: { label: 'SEO', icon: FiTrendingUp, description: 'Site-wide search engine defaults.' },
  footer: { label: 'Footer', icon: FiAlignLeft, description: 'Footer copy and legal lines.' },
  scripts: { label: 'Scripts', icon: FiCode, description: 'Analytics and third-party snippets. Edit with care — bad markup can break the site.' },
};

const groupMeta = (name) =>
  GROUP_META[name] || {
    label: name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    icon: FiSettings,
    description: '',
  };

/** Boolean settings are stored as text, so accept every reasonable truthy spelling. */
const toBoolValue = (value) => value === true || value === 1 || value === '1' || value === 'true';

const editorValue = (setting) => {
  if (setting.type === 'boolean') return toBoolValue(setting.value);
  if (setting.type === 'json') {
    if (!setting.value) return '';
    if (typeof setting.value === 'object') return JSON.stringify(setting.value, null, 2);
    try {
      return JSON.stringify(JSON.parse(setting.value), null, 2);
    } catch {
      return String(setting.value);
    }
  }
  return setting.value ?? '';
};

const apiValue = (type, value) => {
  if (type === 'boolean') return value ? '1' : '0';
  return value ?? '';
};

const SiteSettingsPage = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeGroup, setActiveGroup] = useState(null);
  const [values, setValues] = useState({});
  const [baseline, setBaseline] = useState({});
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [jsonErrors, setJsonErrors] = useState({});

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await settingsService.getAll();
      const loadedGroups = response.data.groups;
      setGroups(loadedGroups);
      setActiveGroup((current) => current || loadedGroups[0]?.name || null);

      const initial = {};
      loadedGroups.forEach((group) => {
        group.settings.forEach((setting) => {
          initial[setting.settingKey] = editorValue(setting);
        });
      });
      setValues(initial);
      setBaseline(initial);
      setJsonErrors({});
    } catch (error) {
      setLoadError(getApiError(error, 'Failed to load the site settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const allSettings = useMemo(() => groups.flatMap((group) => group.settings), [groups]);

  const changedKeys = useMemo(
    () => Object.keys(values).filter((key) => values[key] !== baseline[key]),
    [values, baseline]
  );

  const dirty = changedKeys.length > 0;

  const searchTerm = search.trim().toLowerCase();

  const visibleSettings = useMemo(() => {
    if (searchTerm) {
      return allSettings.filter(
        (setting) =>
          setting.label.toLowerCase().includes(searchTerm) ||
          setting.settingKey.toLowerCase().includes(searchTerm)
      );
    }
    return groups.find((group) => group.name === activeGroup)?.settings || [];
  }, [searchTerm, allSettings, groups, activeGroup]);

  const setValue = (setting, value) => {
    setValues((prev) => ({ ...prev, [setting.settingKey]: value }));

    if (setting.type === 'json') {
      setJsonErrors((prev) => {
        const next = { ...prev };
        if (!String(value).trim()) delete next[setting.settingKey];
        else {
          try {
            JSON.parse(value);
            delete next[setting.settingKey];
          } catch {
            next[setting.settingKey] = 'Invalid JSON';
          }
        }
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (Object.keys(jsonErrors).length) {
      toast.error('Fix the invalid JSON before saving');
      return;
    }

    const payload = changedKeys.reduce((acc, key) => {
      const setting = allSettings.find((item) => item.settingKey === key);
      acc[key] = apiValue(setting?.type, values[key]);
      return acc;
    }, {});

    setSaving(true);
    try {
      await settingsService.update(payload);
      setBaseline(values);
      toast.success(`${changedKeys.length} ${changedKeys.length === 1 ? 'setting' : 'settings'} saved`);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to save the settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setValues(baseline);
    setJsonErrors({});
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <Spinner size="large" />
        <p>Loading site settings…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <Card>
        <div className="ss-error">
          <FiAlertTriangle />
          <h3>Could not load the settings</h3>
          <p>{loadError}</p>
          <Button onClick={fetchSettings}>Try again</Button>
        </div>
      </Card>
    );
  }

  const active = groups.find((group) => group.name === activeGroup);
  const activeMeta = active ? groupMeta(active.name) : null;

  return (
    <div className="site-settings-page">
      <Card
        title="Site Settings"
        subtitle="Global values used across the website — contact details, socials, footer copy and scripts."
        action={
          <div className="ss-header-actions">
            <Button variant="ghost" icon={<FiRotateCcw />} onClick={handleReset} disabled={!dirty || saving}>
              Reset
            </Button>
            <Button icon={<FiSave />} onClick={handleSave} loading={saving} disabled={!dirty}>
              Save changes
            </Button>
          </div>
        }
      >
        {groups.length === 0 ? (
          <div className="ss-empty">
            <FiInbox />
            <p>No settings have been created yet. Run the CMS seeder on the backend to populate them.</p>
          </div>
        ) : (
          <div className="ss-layout">
            <aside className="ss-nav">
              <div className="ss-search">
                <FiSearch />
                <input
                  type="text"
                  value={search}
                  placeholder="Search all settings…"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <ul className="ss-tabs">
                {groups.map((group) => {
                  const meta = groupMeta(group.name);
                  const changedInGroup = group.settings.filter((setting) => changedKeys.includes(setting.settingKey)).length;
                  return (
                    <li key={group.name}>
                      <button
                        type="button"
                        className={`ss-tab ${!searchTerm && activeGroup === group.name ? 'active' : ''}`}
                        onClick={() => {
                          setSearch('');
                          setActiveGroup(group.name);
                        }}
                      >
                        <meta.icon className="ss-tab-icon" />
                        <span className="ss-tab-label">{meta.label}</span>
                        {changedInGroup > 0 ? (
                          <span className="ss-tab-badge changed">{changedInGroup}</span>
                        ) : (
                          <span className="ss-tab-badge">{group.settings.length}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            <section className="ss-panel">
              <header className="ss-panel-head">
                {searchTerm ? (
                  <>
                    <h3>Search results</h3>
                    <p>{visibleSettings.length} {visibleSettings.length === 1 ? 'setting matches' : 'settings match'} “{search.trim()}”.</p>
                  </>
                ) : (
                  <>
                    <h3>{activeMeta?.label}</h3>
                    <p>{activeMeta?.description}</p>
                  </>
                )}
              </header>

              {visibleSettings.length === 0 ? (
                <div className="ss-empty">
                  <FiInbox />
                  <p>{searchTerm ? 'Nothing matches that search.' : 'This group has no settings yet.'}</p>
                </div>
              ) : (
                <div className="ss-fields">
                  {visibleSettings.map((setting) => {
                    const isChanged = changedKeys.includes(setting.settingKey);
                    const isWide = ['textarea', 'html', 'json', 'image'].includes(setting.type);
                    return (
                      <div key={setting.settingKey} className={`ss-field ${isWide ? 'wide' : ''} ${isChanged ? 'changed' : ''}`}>
                        <CmsField
                          type={setting.type}
                          label={setting.label}
                          value={values[setting.settingKey]}
                          onChange={(value) => setValue(setting, value)}
                          rows={setting.type === 'json' ? 8 : 4}
                          error={jsonErrors[setting.settingKey]}
                          hint={jsonErrors[setting.settingKey] ? undefined : setting.settingKey}
                          folder="settings"
                        />
                        {searchTerm && (
                          <Badge variant="default" size="small">{groupMeta(setting.groupName).label}</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}
      </Card>

      {dirty && (
        <div className="ss-savebar" role="status">
          <span className="ss-savebar-text">
            <strong>{changedKeys.length}</strong> unsaved {changedKeys.length === 1 ? 'change' : 'changes'}
          </span>
          <div className="ss-savebar-actions">
            <Button variant="ghost" size="small" onClick={handleReset} disabled={saving}>Discard</Button>
            <Button size="small" icon={<FiSave />} onClick={handleSave} loading={saving}>Save changes</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteSettingsPage;
